import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DollarSign, Plus, Edit, Trash2, TrendingUp, TrendingDown,
  Calendar, PieChart, ArrowUpCircle, ArrowDownCircle,
  Wallet, X, Search, Filter, MoreHorizontal, Settings, LayoutGrid, List
} from 'lucide-react';

const API_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

export default function Finance() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'movements');
  
  // Sincronizar tab con URL query param
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'movements';
    setActiveTab(tabFromUrl);
  }, [searchParams]);
  
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'movements') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: newTab });
    }
  };
  
  const [categories, setCategories] = useState([]);
  const [playerCategories, setPlayerCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false); // For filter changes
  
  // Category management
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryViewMode, setCategoryViewMode] = useState('cards'); // 'cards' or 'list'
  
  // Pagination
  // Pagination - Server side
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [summaryPeriod, setSummaryPeriod] = useState('month');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('ingreso'); // ingreso or egreso
  const [editingTransaction, setEditingTransaction] = useState(null);

  const [summaryByPlayer, setSummaryByPlayer] = useState(null);

  // Initial load
  useEffect(() => {
    fetchData(false);
  }, []);
  
  // Filter changes
  useEffect(() => {
    if (loading) return; // Skip initial
    fetchData(true);
  }, [currentPage, filterType, filterCategory, filterDateFrom, filterDateTo, itemsPerPage]);

  useEffect(() => {
    fetchSummary();
  }, [summaryPeriod]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterCategory, filterDateFrom, filterDateTo]);

  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  const fetchData = async (isFilter = false) => {
    if (!isFilter) {
      setLoading(true);
    } else {
      setIsFiltering(true);
    }
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Build params for server-side filtering
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (filterType !== 'all') params.append('type', filterType);
      if (filterCategory !== 'all') params.append('finance_category_id', filterCategory);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      
      const [catRes, txRes, playerCatRes] = await Promise.all([
        fetch(`${API_URL}/api/finance/categories`, { headers }),
        fetch(`${API_URL}/api/finance/transactions?${params.toString()}`, { headers }),
        fetch(`${API_URL}/api/categories`, { headers })
      ]);
      
      const catsData = await catRes.json();
      setCategories(catsData);
      
      const txData = await txRes.json();
      setTransactions(txData.data || []);
      setTotalItems(txData.pagination?.total || 0);
      setTotalPages(txData.pagination?.total_pages || 1);
      
      setPlayerCategories(await playerCatRes.json());
      
      // Seed categories if empty
      if (catsData.length === 0) {
        await fetch(`${API_URL}/api/finance/seed-categories`, { 
          method: 'POST', 
          headers 
        });
        const newCats = await fetch(`${API_URL}/api/finance/categories`, { headers });
        setCategories(await newCats.json());
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
    setLoading(false);
    setIsFiltering(false);
  };

  const fetchSummary = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const res = await fetch(`${API_URL}/api/finance/summary?period=${summaryPeriod}`, { headers });
      const summaryData = await res.json();
      setSummary(summaryData);
      
      // Also fetch top players by investment
      const dateFrom = summaryData.period_start;
      const dateTo = summaryData.period_end;
      const playerRes = await fetch(
        `${API_URL}/api/finance/summary/by-player?date_from=${dateFrom}&date_to=${dateTo}&limit=10`,
        { headers }
      );
      setSummaryByPlayer(await playerRes.json());
    } catch (error) {
      console.error(error);
    }
  };

  // Server handles filtering, transactions is already filtered
  // Calculate totals from current page transactions (totals for entire filter shown in summary)

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getCategoryGroup = (id) => categories.find(c => c.id === id)?.group || '-';
  const getPlayerCategoryName = (id) => playerCategories.find(c => c.id === id)?.name || '';

  const handleSaveTransaction = async (data) => {
    try {
      const url = editingTransaction 
        ? `${API_URL}/api/finance/transactions/${editingTransaction.id}`
        : `${API_URL}/api/finance/transactions`;
      const method = editingTransaction ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!res.ok) throw new Error((await res.json()).detail);
      
      toast.success(editingTransaction ? 'Movimiento actualizado' : 'Movimiento registrado');
      setShowModal(false);
      setEditingTransaction(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try {
      const res = await fetch(`${API_URL}/api/finance/transactions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Movimiento eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Category management functions
  const handleSaveCategory = async (data) => {
    try {
      const url = editingCategory 
        ? `${API_URL}/api/finance/categories/${editingCategory.id}`
        : `${API_URL}/api/finance/categories`;
      const method = editingCategory ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (!res.ok) throw new Error((await res.json()).detail);
      
      toast.success(editingCategory ? 'Categoría actualizada' : 'Categoría creada');
      setShowCategoryModal(false);
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría? Los movimientos asociados quedarán sin categoría.')) return;
    try {
      const res = await fetch(`${API_URL}/api/finance/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Categoría eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-UY', { 
      style: 'currency', 
      currency: 'UYU',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const openNewTransaction = (type) => {
    setModalType(type);
    setEditingTransaction(null);
    setShowModal(true);
  };

  const openEditTransaction = (tx) => {
    setModalType(tx.type);
    setEditingTransaction(tx);
    setShowModal(true);
  };

  // Calculate totals for current filter
  const totals = transactions.reduce((acc, tx) => {
    if (tx.type === 'ingreso') acc.ingresos += tx.amount;
    else acc.egresos += tx.amount;
    return acc;
  }, { ingresos: 0, egresos: 0 });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="finance-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Finanzas</h1>
            <p className="text-gray-500 mt-1">Comisiones e inversiones de la agencia</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => openNewTransaction('ingreso')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-income-btn"
            >
              <ArrowDownCircle className="h-4 w-4 mr-2" />
              Registrar Ingreso
            </Button>
            <Button 
              onClick={() => openNewTransaction('egreso')}
              variant="destructive"
              data-testid="add-expense-btn"
            >
              <ArrowUpCircle className="h-4 w-4 mr-2" />
              Registrar Egreso
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-gray-100 p-1">
            <TabsTrigger value="movements" className="data-[state=active]:bg-white">
              <Wallet className="h-4 w-4 mr-2" />
              Movimientos
            </TabsTrigger>
            <TabsTrigger value="summary" className="data-[state=active]:bg-white">
              <PieChart className="h-4 w-4 mr-2" />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-white">
              <Settings className="h-4 w-4 mr-2" />
              Categorías
            </TabsTrigger>
          </TabsList>

          {/* Movimientos Tab */}
          <TabsContent value="movements" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="w-40">
                    <Label className="text-xs text-gray-500">Tipo</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="ingreso">Ingresos</SelectItem>
                        <SelectItem value="egreso">Egresos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs text-gray-500">Categoría</Label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs text-gray-500">Desde</Label>
                    <Input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs text-gray-500">Hasta</Label>
                    <Input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  {(filterType !== 'all' || filterCategory !== 'all' || filterDateFrom || filterDateTo) && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setFilterType('all');
                        setFilterCategory('all');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Ingresos</p>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.ingresos)}</p>
                    </div>
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Egresos</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.egresos)}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded-lg">
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Balance</p>
                      <p className={`text-2xl font-bold ${totals.ingresos - totals.egresos >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(totals.ingresos - totals.egresos)}
                      </p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <DollarSign className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transactions List */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  Listado de Movimientos
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({totalItems} registros)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {totalItems === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No hay movimientos registrados</p>
                    <p className="text-sm mt-1">Registra tu primer ingreso o egreso</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {transactions.map(tx => (
                      <div 
                        key={tx.id}
                        className="flex items-center gap-3 p-2 rounded-lg border hover:bg-gray-50 transition-colors"
                      >
                        {/* Icon */}
                        <div className={`p-1.5 rounded-full ${
                          tx.type === 'ingreso' ? 'bg-emerald-100' : 'bg-red-100'
                        }`}>
                          {tx.type === 'ingreso' 
                            ? <ArrowDownCircle className="h-4 w-4 text-emerald-600" />
                            : <ArrowUpCircle className="h-4 w-4 text-red-600" />
                          }
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900 truncate">
                              {tx.description}
                            </span>
                            {tx.player_name && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                👤 {tx.player_name}
                              </Badge>
                            )}
                            {tx.material_name && (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                📦 {tx.material_name} x{tx.material_quantity || 1}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{getCategoryGroup(tx.finance_category_id)}</span>
                            <span>•</span>
                            <span>{getCategoryName(tx.finance_category_id)}</span>
                            {tx.reference && (
                              <>
                                <span>•</span>
                                <span>Ref: {tx.reference}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Date */}
                        <div className="text-xs text-gray-500 hidden md:block">
                          {formatDate(tx.date)}
                        </div>

                        {/* Amount */}
                        <div className={`text-sm font-semibold tabular-nums ${
                          tx.type === 'ingreso' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {tx.type === 'ingreso' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openEditTransaction(tx)}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteTransaction(tx.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemName="transacciones"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            {/* Period Selector */}
            <div className="flex gap-2">
              {[
                { value: 'day', label: 'Hoy' },
                { value: 'week', label: 'Esta Semana' },
                { value: 'month', label: 'Este Mes' },
                { value: 'year', label: 'Este Año' }
              ].map(period => (
                <Button
                  key={period.value}
                  variant={summaryPeriod === period.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSummaryPeriod(period.value)}
                >
                  {period.label}
                </Button>
              ))}
            </div>

            {summary && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-500 mb-1">Ingresos del Período</p>
                        <p className="text-3xl font-bold text-emerald-600">{formatCurrency(summary.total_income)}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-500 mb-1">Egresos del Período</p>
                        <p className="text-3xl font-bold text-red-600">{formatCurrency(summary.total_expense)}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-500 mb-1">Balance del Período</p>
                        <p className={`text-3xl font-bold ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(summary.balance)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Breakdown by Group */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                        Ingresos por Categoría
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {summary.income_by_group.length > 0 ? (
                        <div className="space-y-3">
                          {summary.income_by_group.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                <span className="text-gray-700">{item.name}</span>
                              </div>
                              <span className="font-semibold text-emerald-600">{formatCurrency(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Sin datos en este período</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingDown className="h-5 w-5 text-red-600" />
                        Egresos por Categoría
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {summary.expense_by_group.length > 0 ? (
                        <div className="space-y-3">
                          {summary.expense_by_group.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                <span className="text-gray-700">{item.name}</span>
                              </div>
                              <span className="font-semibold text-red-600">{formatCurrency(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Sin datos en este período</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Players by Investment */}
                {summaryByPlayer && summaryByPlayer.top_players && summaryByPlayer.top_players.length > 0 && (
                  <Card data-testid="top-players-investment">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-blue-600" />
                        Top Jugadores por Inversión
                      </CardTitle>
                      <p className="text-xs text-gray-500">
                        Total invertido en {summaryByPlayer.players_count} jugador{summaryByPlayer.players_count !== 1 ? 'es' : ''}: {formatCurrency(summaryByPlayer.total_invested)}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {summaryByPlayer.top_players.map((p, idx) => (
                          <div key={p.player_id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-bold text-gray-400 w-6">#{idx + 1}</span>
                              <a 
                                href={`/players/${p.player_id}`}
                                className="font-medium text-gray-900 hover:text-brand-600"
                              >
                                {p.player_name}
                              </a>
                              <span className="text-xs text-gray-500">
                                {p.count} movimiento{p.count !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <span className="font-semibold text-red-600">{formatCurrency(p.total)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Categorías de Finanzas</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Organiza tus ingresos y egresos por categorías</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* View Toggle */}
                    <div className="flex border rounded-lg overflow-hidden">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCategoryViewMode('cards')}
                        className={`p-2 rounded-none ${categoryViewMode === 'cards' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCategoryViewMode('list')}
                        className={`p-2 rounded-none ${categoryViewMode === 'list' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Nueva Categoría
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium text-gray-600">No hay categorías</p>
                    <p className="text-sm mt-2">Crea categorías para organizar tus movimientos financieros</p>
                  </div>
                ) : categoryViewMode === 'list' ? (
                  /* List View */
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grupo</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {categories.map(cat => (
                          <tr key={cat.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                            <td className="px-4 py-3">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${cat.type === 'ingreso' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                              >
                                {cat.type === 'ingreso' ? 'Ingreso' : 'Egreso'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">{cat.group || '-'}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8"
                                  onClick={() => { setEditingCategory(cat); setShowCategoryModal(true); }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteCategory(cat.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Cards View */
                  <div className="space-y-6">
                    {/* Ingresos */}
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                        <ArrowDownCircle className="h-4 w-4" />
                        Categorías de Ingresos
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {categories.filter(c => c.type === 'ingreso').map(cat => (
                          <Card key={cat.id} className="hover:shadow-md transition-shadow border-emerald-100">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{cat.name}</h4>
                                  {cat.group && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {cat.group}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => { setEditingCategory(cat); setShowCategoryModal(true); }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteCategory(cat.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {categories.filter(c => c.type === 'ingreso').length === 0 && (
                          <p className="text-sm text-gray-400 col-span-full">No hay categorías de ingresos</p>
                        )}
                      </div>
                    </div>

                    {/* Egresos */}
                    <div>
                      <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                        <ArrowUpCircle className="h-4 w-4" />
                        Categorías de Egresos
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {categories.filter(c => c.type === 'egreso').map(cat => (
                          <Card key={cat.id} className="hover:shadow-md transition-shadow border-red-100">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{cat.name}</h4>
                                  {cat.group && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {cat.group}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => { setEditingCategory(cat); setShowCategoryModal(true); }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteCategory(cat.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        {categories.filter(c => c.type === 'egreso').length === 0 && (
                          <p className="text-sm text-gray-400 col-span-full">No hay categorías de egresos</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Transaction Modal */}
        {showModal && (
          <TransactionModal
            type={modalType}
            transaction={editingTransaction}
            categories={categories}
            playerCategories={playerCategories}
            onSave={handleSaveTransaction}
            onClose={() => { setShowModal(false); setEditingTransaction(null); }}
          />
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <FinanceCategoryModal
            category={editingCategory}
            onSave={handleSaveCategory}
            onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          />
        )}
      </div>
    </Layout>
  );
}

// Transaction Modal Component
function TransactionModal({ type, transaction, categories, playerCategories, onSave, onClose }) {
  const [formData, setFormData] = useState({
    date: transaction?.date || new Date().toISOString().split('T')[0],
    type: transaction?.type || type,
    finance_category_id: transaction?.finance_category_id || '',
    amount: transaction?.amount || '',
    description: transaction?.description || '',
    player_category_id: transaction?.player_category_id || '',
    player_id: transaction?.player_id || '',
    material_item_id: transaction?.material_item_id || '',
    material_quantity: transaction?.material_quantity || 1,
    reference: transaction?.reference || '',
    payment_method: transaction?.payment_method || '',
    person_entity: transaction?.person_entity || '',
    notes: transaction?.notes || ''
  });
  
  // State for player and material search
  const [associatePlayer, setAssociatePlayer] = useState(!!transaction?.player_id);
  const [associateMaterial, setAssociateMaterial] = useState(!!transaction?.material_item_id);
  const [players, setPlayers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [materialSearch, setMaterialSearch] = useState('');
  
  const API_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
  
  // Load players and materials for egresos
  useEffect(() => {
    if (formData.type === 'egreso') {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      fetch(`${API_URL}/api/players?limit=500`, { headers })
        .then(res => res.json())
        .then(data => setPlayers(data.data || []))
        .catch(err => console.error('Error loading players:', err));
        
      fetch(`${API_URL}/api/materials/items?limit=500`, { headers })
        .then(res => res.json())
        .then(data => setMaterials(data.data || []))
        .catch(err => console.error('Error loading materials:', err));
    }
  }, [formData.type, API_URL]);

  const filteredCategories = categories.filter(c => c.type === formData.type);
  const groupedCategories = filteredCategories.reduce((acc, cat) => {
    if (!acc[cat.group]) acc[cat.group] = [];
    acc[cat.group].push(cat);
    return acc;
  }, {});
  
  // Filter players and materials
  const filteredPlayers = players.filter(p => {
    if (!playerSearch) return true;
    const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
    return fullName.includes(playerSearch.toLowerCase());
  }).slice(0, 30);
  
  const filteredMaterials = materials.filter(m => {
    const hasPrice = m.unit_price && m.unit_price > 0;
    const hasStock = m.quantity && m.quantity > 0;
    if (!materialSearch) return hasPrice && hasStock;
    return hasPrice && hasStock && m.name.toLowerCase().includes(materialSearch.toLowerCase());
  }).slice(0, 30);
  
  const selectedPlayer = players.find(p => p.id === formData.player_id);
  const selectedMaterial = materials.find(m => m.id === formData.material_item_id);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.finance_category_id) {
      toast.error('Selecciona una categoría');
      return;
    }
    
    // If associating material, validate
    if (associateMaterial && !formData.material_item_id) {
      toast.error('Selecciona un material');
      return;
    }
    
    // If associating player, validate
    if (associatePlayer && !formData.player_id) {
      toast.error('Selecciona un jugador');
      return;
    }
    
    // If not using material, validate amount
    if (!associateMaterial && (!formData.amount || formData.amount <= 0)) {
      toast.error('Ingresa un monto válido');
      return;
    }
    
    const submitData = {
      ...formData,
      amount: associateMaterial ? undefined : parseFloat(formData.amount), // Backend calculates if material
      player_category_id: formData.player_category_id === 'none' ? null : (formData.player_category_id || null),
      player_id: associatePlayer ? formData.player_id : null,
      material_item_id: associateMaterial ? formData.material_item_id : null,
      material_quantity: associateMaterial ? parseInt(formData.material_quantity) || 1 : null
    };
    
    onSave(submitData);
  };

  const isIncome = formData.type === 'ingreso';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isIncome ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isIncome ? 'bg-emerald-100' : 'bg-red-100'}`}>
                {isIncome 
                  ? <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
                  : <ArrowUpCircle className="h-5 w-5 text-red-600" />
                }
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {transaction ? 'Editar' : 'Registrar'} {isIncome ? 'Ingreso' : 'Egreso'}
                </h2>
                <p className="text-sm text-gray-500">
                  {isIncome ? 'Dinero que entra al club' : 'Dinero que sale del club'}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Amount - Most important field */}
          <div>
            <Label className="text-sm font-medium">Monto {!associateMaterial && '*'}</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              {associateMaterial && selectedMaterial ? (
                <div className="pl-8 text-2xl font-semibold h-14 flex items-center bg-gray-100 rounded-md border">
                  {((selectedMaterial.unit_price || 0) * (parseInt(formData.material_quantity) || 1)).toLocaleString()}
                  <span className="text-sm text-gray-500 ml-2">(calculado del material)</span>
                </div>
              ) : (
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="pl-8 text-2xl font-semibold h-14"
                  placeholder="0"
                  step="0.01"
                  min="0"
                  required={!associateMaterial}
                  autoFocus
                />
              )}
            </div>
          </div>

          {/* Date and Category Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Fecha *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Forma de Pago</Label>
              <Select 
                value={formData.payment_method} 
                onValueChange={(v) => setFormData({...formData, payment_method: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label className="text-sm font-medium">Categoría *</Label>
            <Select 
              value={formData.finance_category_id} 
              onValueChange={(v) => setFormData({...formData, finance_category_id: v})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar categoría..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupedCategories).map(([group, cats]) => (
                  <div key={group}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                      {group}
                    </div>
                    {cats.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium">Descripción {!associateMaterial && '*'}</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="mt-1"
              placeholder="Ej: Pago de sueldo mes de enero, Venta de entradas partido vs River..."
              required={!associateMaterial}
            />
          </div>
          
          {/* Player and Material association - Only for Egresos */}
          {!isIncome && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
              <p className="text-sm font-medium text-gray-700">Opciones de inversión</p>
              
              {/* Associate to Player */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={associatePlayer}
                    onChange={(e) => {
                      setAssociatePlayer(e.target.checked);
                      if (!e.target.checked) setFormData({...formData, player_id: ''});
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Asociar a jugador (suma a su inversión)</span>
                </label>
                
                {associatePlayer && (
                  <div className="ml-6 border rounded-lg p-3 bg-blue-50">
                    <Input
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      className="mb-2"
                      placeholder="Buscar jugador..."
                    />
                    <div className="max-h-32 overflow-y-auto border rounded bg-white">
                      {filteredPlayers.length === 0 ? (
                        <p className="p-2 text-sm text-gray-500 text-center">No se encontraron jugadores</p>
                      ) : (
                        filteredPlayers.map(p => (
                          <div
                            key={p.id}
                            className={`p-2 cursor-pointer hover:bg-blue-100 flex justify-between ${
                              formData.player_id === p.id ? 'bg-blue-200' : ''
                            }`}
                            onClick={() => setFormData({...formData, player_id: p.id})}
                          >
                            <span>{p.first_name} {p.last_name}</span>
                            {p.total_investment > 0 && (
                              <span className="text-xs text-gray-500">${p.total_investment?.toLocaleString()}</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    {selectedPlayer && (
                      <p className="text-xs text-blue-600 mt-2">
                        ✓ {selectedPlayer.first_name} {selectedPlayer.last_name} seleccionado
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Associate Material */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={associateMaterial}
                    onChange={(e) => {
                      setAssociateMaterial(e.target.checked);
                      if (!e.target.checked) setFormData({...formData, material_item_id: '', material_quantity: 1});
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Descontar del stock de materiales</span>
                </label>
                
                {associateMaterial && (
                  <div className="ml-6 border rounded-lg p-3 bg-orange-50">
                    <Input
                      value={materialSearch}
                      onChange={(e) => setMaterialSearch(e.target.value)}
                      className="mb-2"
                      placeholder="Buscar material..."
                    />
                    <div className="max-h-32 overflow-y-auto border rounded bg-white">
                      {filteredMaterials.length === 0 ? (
                        <p className="p-2 text-sm text-gray-500 text-center">No hay materiales con precio y stock</p>
                      ) : (
                        filteredMaterials.map(m => (
                          <div
                            key={m.id}
                            className={`p-2 cursor-pointer hover:bg-orange-100 flex justify-between ${
                              formData.material_item_id === m.id ? 'bg-orange-200' : ''
                            }`}
                            onClick={() => setFormData({...formData, material_item_id: m.id})}
                          >
                            <span>{m.name}</span>
                            <span className="text-xs text-gray-500">${m.unit_price?.toLocaleString()} · Stock: {m.quantity}</span>
                          </div>
                        ))
                      )}
                    </div>
                    {selectedMaterial && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Cantidad:</Label>
                          <Input
                            type="number"
                            min="1"
                            max={selectedMaterial.quantity}
                            value={formData.material_quantity}
                            onChange={(e) => setFormData({...formData, material_quantity: e.target.value})}
                            className="w-20"
                          />
                        </div>
                        <p className="text-xs text-orange-600">
                          Total: ${((selectedMaterial.unit_price || 0) * (parseInt(formData.material_quantity) || 1)).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Optional Fields */}
          <div>
            <Label className="text-sm font-medium">Referencia</Label>
            <Input
              value={formData.reference}
              onChange={(e) => setFormData({...formData, reference: e.target.value})}
              className="mt-1"
              placeholder="Nº factura, recibo..."
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Persona / Entidad</Label>
            <Input
              value={formData.person_entity}
              onChange={(e) => setFormData({...formData, person_entity: e.target.value})}
              className="mt-1"
              placeholder="Nombre de la persona o empresa..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className={`flex-1 ${isIncome ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {transaction ? 'Guardar Cambios' : 'Registrar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Finance Category Modal Component
function FinanceCategoryModal({ category, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: category?.name || "",
    type: category?.type || "ingreso",
    group: category?.group || ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {category ? "Editar Categoría" : "Nueva Categoría"}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="mt-1"
              placeholder="Ej: Cuotas mensuales, Equipamiento..."
              required
            />
          </div>
          
          <div>
            <Label>Tipo *</Label>
            <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v})}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ingreso">Ingreso</SelectItem>
                <SelectItem value="egreso">Egreso</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Grupo (opcional)</Label>
            <Input
              value={formData.group}
              onChange={(e) => setFormData({...formData, group: e.target.value})}
              className="mt-1"
              placeholder="Ej: Operativos, Deportivos..."
            />
            <p className="text-xs text-gray-500 mt-1">Para agrupar categorías similares</p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              {category ? "Guardar Cambios" : "Crear Categoría"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
